import type { CompanyImage, GalleryItem, GalleryItemType } from "@/modules/companies/api/types"
import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useCallback, useState } from "react"

import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/dist/module/components/Layout/Layout"

import { MediaImage } from "@/features/companyDetail/components/shared/Media/Image"
import { isVideoUrlValid } from "@/features/companyDetail/components/shared/Media/utils/isVideoUrlValid"
import { MediaVideo } from "@/features/companyDetail/components/shared/Media/Video"
import { MEDIA_TYPE_IMAGE, MEDIA_TYPE_VIDEO } from "@/modules/companies/constants/enums"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./TeamsJobPositions.module.css"

const ImagesModalDynamic = dynamic(
  () => import("@/features/companyDetail/components/shared/MediaGallery/ImagesModal").then(mod => mod.ImagesModal),
  { ssr: false }
)

export const TeamsJobPositions: FunctionComponent = () => {
  const [selectedMediaImageIndex, setSelectedMediaImageIndex] = useState<number>(-1)
  const tracking = useTracking()
  const { dataCompany, isSuccessCompany } = useCompanyFetch()

  const displayModalMediaImage = useCallback(
    (index: number) => {
      tracking?.track("see_more_information_click", {
        company_id: dataCompany?.id || "",
        company_name: dataCompany?.page.name || "",
        context: "images_teams",
        service: "company",
      })
      setSelectedMediaImageIndex(index)
    },
    [tracking, dataCompany?.page.name, dataCompany?.id]
  )

  const closeModal = useCallback((): void => setSelectedMediaImageIndex(-1), [])

  if (!dataCompany || !isSuccessCompany) return null

  const {
    id: companyId,
    page: { name, jobPositions },
  } = dataCompany

  const mediaJobPositions: GalleryItem<GalleryItemType>[] =
    jobPositions.map(jobPosition => ({
      description: jobPosition.name,
      id: jobPosition.jobRoleId,
      previewUrl: jobPosition.video.previewUrl,
      type: jobPosition.image.url ? MEDIA_TYPE_IMAGE : MEDIA_TYPE_VIDEO,
      url: jobPosition.image.url || jobPosition.video.url,
    })) || []

  const galleryImages: CompanyImage[] = mediaJobPositions.filter(
    jobPosition => jobPosition.type === MEDIA_TYPE_IMAGE
  ) as GalleryItem<"MEDIA_TYPE_IMAGE">[]

  const galleryImagesIndexes = mediaJobPositions.reduce((acc, jobPosition, index) => {
    if (jobPosition.type === MEDIA_TYPE_IMAGE) {
      acc[index] = Object.keys(acc).length
    }
    return acc
  }, {} as Record<number, number>)

  return (
    (mediaJobPositions.length > 0 && (
      <Layout colorBlock={LAYOUT_COLOR_BLOCK.WHITE}>
        <Layout.Column>
          <div className={styles.jobPositions}>
            {mediaJobPositions.map(({ type, description, id, url, previewUrl }, jobPositionIndex) => {
              if (type === MEDIA_TYPE_IMAGE) {
                return (
                  <div
                    key={url}
                    className={styles.jobPositions__image}
                    onClick={() => displayModalMediaImage(galleryImagesIndexes[jobPositionIndex])}
                    onKeyDown={() => displayModalMediaImage(galleryImagesIndexes[jobPositionIndex])}
                    role="button"
                    tabIndex={0}
                  >
                    <MediaImage alt={description} src={url} />
                  </div>
                )
              }
              if (type === MEDIA_TYPE_VIDEO && isVideoUrlValid(url)) {
                return (
                  <MediaVideo
                    key={url}
                    video={{
                      description,
                      id,
                      previewUrl,
                      url,
                    }}
                    tracking={{ id: companyId, name }}
                  />
                )
              }
              return null
            })}
          </div>
          {selectedMediaImageIndex > -1 && (
            <ImagesModalDynamic gallery={galleryImages} initialIndex={selectedMediaImageIndex} onClose={closeModal} />
          )}
        </Layout.Column>
      </Layout>
    )) ||
    null
  )
}
